import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, List, Empty, Skeleton, Tag, Typography, Avatar, Button, Space } from 'antd';
import { Link } from 'react-router-dom';
import {
  UserOutlined,
  ProjectOutlined,
  CheckSquareOutlined,
  CalendarOutlined,
  BellOutlined,
  TeamOutlined,
  ReloadOutlined,
  FileTextOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { dashboardApi } from '../../utils/api';
import { readCachedAuthUserId } from '../../utils/cachedAuthUser';
import { handleApiError } from '../../utils/errorHandler';
import {
  followUpStatusLabel,
  formatFollowUpDate,
  resolveFollowUpEntity,
  resolveFollowUpActivityHref,
  buildPendingFollowUpsListPath,
  FOLLOW_UPS_LIST_RETURN_STORAGE_KEY,
} from '../../utils/followUpDisplayUtils';
import { buildListToDetailNavigationState } from '../../utils/breadcrumbTrail';

const { Text } = Typography;

/** ارتفاع ثابت باکس پیگیری‌های معلق — overflow داخل همین ناحیه اسکرول می‌شود */
const PENDING_FOLLOWUPS_PANEL_HEIGHT_PX = 360;

const buildViewAllFollowUpsPath = () => buildPendingFollowUpsListPath(readCachedAuthUserId());

const buildFollowUpOpenState = (item) => {
  const entity = resolveFollowUpEntity(item);
  const href = resolveFollowUpActivityHref(item) || entity.href;
  if (!href) return { href: null, entity, state: undefined };
  const listPath = buildViewAllFollowUpsPath();
  return {
    href,
    entity,
    state: buildListToDetailNavigationState({
      listSnapshot: {
        filters: {
          status: 'pending',
          assign_to: readCachedAuthUserId() ?? 'all',
          follow_up_activity_id: 'all',
        },
        searchText: '',
        pagination: { current: 1, pageSize: 10 },
      },
      returnTo: listPath,
      listPath,
      listLabel: 'همه فعالیت‌ها',
      entityPath: href,
      entityLabel: entity.name !== '—' ? entity.name : 'فعالیت',
    }),
  };
};

const toneClassMap = {
  blue: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300',
  green: 'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-300',
  orange: 'bg-orange-50 text-orange-600 dark:bg-orange-900/30 dark:text-orange-300',
  pink: 'bg-pink-50 text-pink-600 dark:bg-pink-900/30 dark:text-pink-300',
  purple: 'bg-purple-50 text-purple-600 dark:bg-purple-900/30 dark:text-purple-300',
};

const StatCard = ({ title, value, icon, tone = 'blue' }) => (
  <Card className="border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-all bg-white dark:bg-gray-800">
    <div className="flex items-center justify-between gap-3">
      <div>
        <div className="text-xs text-gray-500">{title}</div>
        <div className="mt-1 text-2xl font-extrabold text-gray-800 dark:text-gray-100 tracking-tight">{value}</div>
      </div>
      <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${toneClassMap[tone] || toneClassMap.blue}`}>
        {icon}
      </div>
    </div>
  </Card>
);

const EmptyState = ({ title, desc }) => (
  <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-8 text-center dark:border-gray-700 dark:bg-gray-900/40">
    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
      <BellOutlined className="text-gray-500" />
    </div>
    <div className="text-base font-semibold text-gray-700 dark:text-gray-200">{title}</div>
    <div className="mt-1 text-sm text-gray-500">{desc}</div>
  </div>
);

const PrivateDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState('employee');
  const [raw, setRaw] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dashboardApi.getPersonal();
      setType(res?.type || 'employee');
      const payload = res?.data || {};
      setRaw(payload);
    } catch (error) {
      setRaw(null);
      handleApiError(error, 'خطا در دریافت داشبورد خصوصی');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const employeeStats = useMemo(() => {
    if (!raw) return [];
    return [
      { title: 'پروژه‌ها', value: raw.totalProjects ?? 0, icon: <ProjectOutlined />, tone: 'blue' },
      { title: 'تسک تکمیل‌شده', value: raw?.counts?.totalCompletedTasks ?? 0, icon: <CheckSquareOutlined />, tone: 'green' },
      { title: 'تیکت باز', value: raw.totalOpenTickets ?? 0, icon: <FileTextOutlined />, tone: 'orange' },
      { title: 'پیگیری معلق', value: raw.pendingFollowups?.length ?? 0, icon: <BellOutlined />, tone: 'pink' },
    ];
  }, [raw]);

  const clientStats = useMemo(() => {
    if (!raw) return [];
    return [
      { title: 'پروژه‌ها', value: raw?.counts?.totalProjects ?? 0, icon: <ProjectOutlined />, tone: 'blue' },
      { title: 'تیکت حل‌نشده', value: raw?.counts?.totalUnResolvedTickets ?? 0, icon: <FileTextOutlined />, tone: 'orange' },
      { title: 'فاکتور پرداخت‌شده', value: raw?.totalPaidInvoice ?? 0, icon: <CheckSquareOutlined />, tone: 'green' },
      { title: 'قرارداد امضاشده', value: raw?.totalContractsSigned ?? 0, icon: <TeamOutlined />, tone: 'purple' },
    ];
  }, [raw]);

  const stats = type === 'client' ? clientStats : employeeStats;
  const notices = raw?.notices || [];
  const followups = raw?.pendingFollowups || [];
  const birthdays = raw?.upcomingBirthdays || [];

  return (
    <MainLayout>
      <div className="custom-font">
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-sky-50 dark:bg-sky-900/25 flex items-center justify-center">
                <UserOutlined className="text-sky-600 dark:text-sky-300 text-xl" />
              </div>
              <div>
                <h1 className="m-0 text-2xl font-bold text-gray-800 dark:text-gray-100">داشبورد خصوصی</h1>
                <Text type="secondary">نمای شخصی از فعالیت‌های روزانه شما</Text>
              </div>
            </div>
            <Space>
              <Tag color={type === 'client' ? 'purple' : 'blue'}>{type === 'client' ? 'مشتری' : 'کارمند'}</Tag>
              <Button icon={<ReloadOutlined />} loading={loading} onClick={fetchData}>بروزرسانی</Button>
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl space-y-6">
            {loading ? (
              <Skeleton active paragraph={{ rows: 10 }} />
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                  {stats.map((s) => (
                    <StatCard key={s.title} {...s} />
                  ))}
                </div>

                {type === 'employee' ? (
                  <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                    <Card title="اعلانات" className="xl:col-span-1">
                      {notices.length ? (
                        <List
                          size="small"
                          dataSource={notices}
                          renderItem={(n) => (
                            <List.Item>
                              <div className="w-full">
                                <div className="font-medium">{n.heading}</div>
                                <Text type="secondary" className="text-xs">{n.notice_date}</Text>
                              </div>
                            </List.Item>
                          )}
                        />
                      ) : <EmptyState title="اعلانی موجود نیست" desc="فعلاً مورد جدیدی برای شما ثبت نشده است." />}
                    </Card>
                    <Card
                      title="پیگیری‌های معلق"
                      className="xl:col-span-2"
                      extra={
                        <Link
                          to={buildViewAllFollowUpsPath()}
                          state={{ returnTo: '/dashboard/private' }}
                          onClick={() => {
                            try {
                              sessionStorage.setItem(FOLLOW_UPS_LIST_RETURN_STORAGE_KEY, '/dashboard/private');
                            } catch {
                              /* ignore */
                            }
                          }}
                        >
                          <Button type="link" size="small" icon={<ArrowLeftOutlined />} className="custom-font">
                            مشاهده همه
                          </Button>
                        </Link>
                      }
                      styles={{ body: { paddingTop: 0 } }}
                    >
                      <div
                        className="overflow-y-auto overflow-x-hidden"
                        style={{ height: PENDING_FOLLOWUPS_PANEL_HEIGHT_PX }}
                      >
                        {followups.length ? (
                          <List
                            size="small"
                            dataSource={followups}
                            renderItem={(f) => {
                              const { href, entity, state } = buildFollowUpOpenState(f);
                              const title = entity.name !== '—' ? entity.name : (f.remark || 'بدون توضیح');
                              return (
                                <List.Item>
                                  <div className="flex w-full items-center justify-between gap-2">
                                    <div className="min-w-0">
                                      {href ? (
                                        <Link
                                          to={href}
                                          state={state}
                                          onClick={() => {
                                            try {
                                              sessionStorage.setItem(
                                                FOLLOW_UPS_LIST_RETURN_STORAGE_KEY,
                                                '/dashboard/private',
                                              );
                                            } catch {
                                              /* ignore */
                                            }
                                          }}
                                          className="font-medium truncate block text-blue-600 hover:text-blue-700 hover:underline"
                                        >
                                          {title}
                                        </Link>
                                      ) : (
                                        <div className="font-medium truncate">{title}</div>
                                      )}
                                      {f.remark && entity.name !== '—' ? (
                                        <div className="text-xs text-gray-500 truncate">{f.remark}</div>
                                      ) : null}
                                      <Text type="secondary" className="text-xs">
                                        {formatFollowUpDate(f.next_follow_up_date)}
                                      </Text>
                                    </div>
                                    <Tag color={f.status === 'completed' ? 'green' : 'gold'}>
                                      {followUpStatusLabel(f.status)}
                                    </Tag>
                                  </div>
                                </List.Item>
                              );
                            }}
                          />
                        ) : (
                          <EmptyState title="پیگیری معلقی وجود ندارد" desc="همه پیگیری‌ها به‌روز هستند." />
                        )}
                      </div>
                    </Card>
                  </div>
                ) : (
                  <Card title="مایلستون‌های در انتظار">
                    {(raw?.pendingMilestones || []).length ? (
                      <List
                        dataSource={raw.pendingMilestones}
                        renderItem={(m) => <List.Item>{m?.title || m?.name || 'مرحله'}</List.Item>}
                      />
                    ) : <EmptyState title="مایلستون در انتظاری وجود ندارد" desc="در این بازه کاری، مورد معلقی ثبت نشده است." />}
                  </Card>
                )}

                {type === 'employee' && (
                  <Card title="تولدهای آینده">
                    {birthdays.length ? (
                      <List
                        dataSource={birthdays.slice(0, 6)}
                        renderItem={(b) => (
                          <List.Item>
                            <List.Item.Meta
                              avatar={<Avatar src={b?.user?.image_url} icon={<CalendarOutlined />} />}
                              title={b?.user?.name || 'کارمند'}
                              description={b?.upcoming_birthday || '-'}
                            />
                          </List.Item>
                        )}
                      />
                    ) : <EmptyState title="تولد نزدیکی ثبت نشده" desc="رویداد تولد جدیدی برای روزهای آینده موجود نیست." />}
                  </Card>
                )}
              </>
            )}
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default PrivateDashboard;
